import { useEffect, useState } from 'react';
import { CheckCircle as CheckCircleIcon, Close as CloseIcon, DragIndicatorSharp, InfoOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UseMutationResult } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Berth, FenderInformation } from '@/api/management/requests';
import Select from '@/components/common/Select/Select';
import { enqueueErrorSnackbar } from '@/components/management/EnqueueErrorSnackbar/EnqueueErrorSnackbar';

interface UpdateBerthDialogProps<TData, TError, TVariables, TContext> {
  open: boolean;
  berth: Berth | undefined;
  mutation: UseMutationResult<TData, TError, TVariables, TContext>;
  getMutationValues: (berth: Berth) => TVariables;
  closeDialog: (disabled: boolean, newBerth: Berth) => void;
}

function EditBerthDialog<TData, TError, TVariables, TContext>({
  open,
  berth,
  mutation,
  getMutationValues,
  closeDialog,
}: UpdateBerthDialogProps<TData, TError, TVariables, TContext>) {
  const [newBerth, setNewBerth] = useState(berth ? { ...berth, fendersInformation: berth.fendersInformation || [] } : berth);
  const snackbarContext = useSnackbar();

  useEffect(() => {
    setNewBerth(berth ? { ...berth, fendersInformation: berth.fendersInformation || [] } : berth);
  }, [berth]);

  const modifyBerth = async () => {
    if (berth) {
      const values = getMutationValues(newBerth);
      await mutation
        .mutateAsync(values)
        .then(() => closeDialog(true, newBerth))
        .catch((error) => {
          closeDialog(false, berth);
          enqueueErrorSnackbar(snackbarContext, `Could not update berth: ${error.body?.message}`, 'toggle-berth-mooring-detection-error');
        });
    } else {
      closeDialog(false, berth);
    }
  };
  return (
    <Dialog maxWidth="md" open={open} onClose={() => closeDialog(false, berth)}>
      <DialogTitle>Modify Berth</DialogTitle>
      <DialogContent sx={{ '& > :not(style)': { m: 1 } }}>
        <form>
          <Grid container spacing={2} display={'flex'} justifyContent={'space-between'}>
            <Grid item xs={5}>
              <TextField
                label="Berth ID"
                value={newBerth?.berthId}
                onChange={(e) => setNewBerth({ ...newBerth, berthId: e.target.value })}
                disabled
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={5} />

            <Grid item xs={5}>
              <TextField
                label="Name"
                value={newBerth?.name}
                onChange={(e) => setNewBerth({ ...newBerth, name: e.target.value })}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={5}>
              <Select
                options={['Quay', 'Jetty', 'Buoy', 'FSRU']}
                label="Type"
                value={newBerth?.type}
                size="small"
                fullWidth={true}
                onChange={(e) => setNewBerth({ ...newBerth, type: e })}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="Latitude"
                value={newBerth?.location?.latitude}
                onChange={(e) =>
                  setNewBerth({
                    ...newBerth,
                    location: { ...newBerth.location, latitude: parseFloat(e.target.value) },
                  })
                }
                type="number"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="Longitude"
                value={newBerth?.location?.longitude}
                onChange={(e) =>
                  setNewBerth({
                    ...newBerth,
                    location: { ...newBerth.location, longitude: parseFloat(e.target.value) },
                  })
                }
                type="number"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="Orientation"
                value={newBerth?.orientation}
                onChange={(e) => setNewBerth({ ...newBerth, orientation: parseInt(e.target.value) })}
                type="number"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Bollard
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="Bollard Capacity"
                value={newBerth?.bollardInformation?.capacity}
                onChange={(e) =>
                  setNewBerth({
                    ...newBerth,
                    bollardInformation: { ...newBerth?.bollardInformation, capacity: parseInt(e.target.value) },
                  })
                }
                type="number"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="Bollard Height"
                value={newBerth?.bollardInformation?.height}
                onChange={(e) =>
                  setNewBerth({
                    ...newBerth,
                    bollardInformation: { ...newBerth?.bollardInformation, height: parseFloat(e.target.value) },
                  })
                }
                type="number"
                fullWidth
                size="small"
              />
            </Grid>
            <EditableFenders
              fenders={newBerth?.fendersInformation || []}
              onChange={(fenders) => setNewBerth({ ...newBerth, fendersInformation: fenders })}
            />
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ pr: 4, pb: 2 }}>
        <Button variant="contained" color="secondary" onClick={() => closeDialog(false, berth)} data-testid="cancel-modify-berth-button">
          <Typography color={'black'}>Cancel</Typography>
        </Button>
        <LoadingButton
          loading={mutation.isLoading}
          variant="contained"
          color="success"
          loadingPosition="end"
          endIcon={<CheckCircleIcon />}
          onClick={modifyBerth}
          disabled={!berth}
          data-testid="modify-berth-button"
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

type EditableFendersProps = {
  fenders: FenderInformation[];
  onChange: (fenders: FenderInformation[]) => void;
};

const SAFE_DEFLECTION_ERROR = 'Safe Deflection must be greater than 0';

const isSafeDeflectionInvalid = (safeDeflection: number | undefined): boolean => {
  return safeDeflection == null || safeDeflection <= 0;
};

const validateSingleFender = (fender: FenderInformation, index: number, errors: { [key: number]: { [field: string]: string } }) => {
  if (isSafeDeflectionInvalid(fender.safeDeflection)) {
    if (!errors[index]) errors[index] = {};
    errors[index].safeDeflection = SAFE_DEFLECTION_ERROR;
  } else if (errors[index]?.safeDeflection) {
    delete errors[index].safeDeflection;
    if (Object.keys(errors[index]).length === 0) {
      delete errors[index];
    }
  }
};

const validateAllFenders = (fenders: FenderInformation[]): { [key: number]: { [field: string]: string } } => {
  const newErrors: { [key: number]: { [field: string]: string } } = {};
  fenders.forEach((fender, index) => {
    validateSingleFender(fender, index, newErrors);
  });
  return newErrors;
};

function EditableFenders({ fenders, onChange }: EditableFendersProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [errors, setErrors] = useState<{ [key: number]: { [field: string]: string } }>({});

  useEffect(() => {
    setErrors(validateAllFenders(fenders));
  }, [fenders]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = Number(active.id);
      const newIndex = Number(over.id);
      const reorderedFenders = arrayMove(fenders, oldIndex, newIndex);
      onChange(reorderedFenders);
      setErrors(validateAllFenders(reorderedFenders));
    }
  };

  const addFender = () => {
    onChange([...fenders, {} as FenderInformation]);
  };

  const addFenderAt = (index: number) => {
    onChange([...fenders.slice(0, index + 1), {} as FenderInformation, ...fenders.slice(index + 1)]);
  };

  const removeFender = (index: number) => {
    const updatedFenders = fenders.filter((_, i) => i !== index);
    onChange(updatedFenders);
  };

  const updateFender = (index: number, field: keyof FenderInformation, value: string | number | undefined) => {
    const newFenders = [...fenders];
    newFenders[index] = { ...newFenders[index], [field]: value };
    onChange(newFenders);
  };

  return (
    <Grid item xs={12}>
      <Grid container display={'flex'} alignItems={'center'} justifyContent={'space-between'} mb={1}>
        <Tooltip title="Only the first fender will be selected for calculation">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6">Fenders</Typography>
            <InfoOutlined color={'disabled'} sx={{ cursor: 'pointer', height: 24, width: 24, ml: 1 }} />
          </div>
        </Tooltip>
        <Button variant={'contained'} onClick={addFender}>
          + new fender
        </Button>
      </Grid>
      <Grid container direction="column" spacing={1} sx={{ mt: 1 }}>
        {fenders.length > 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={fenders.map((_, i) => i.toString())} strategy={verticalListSortingStrategy}>
              {fenders.map((fender, index) => (
                <SortableFender
                  key={index}
                  fender={fender}
                  index={index}
                  updateFender={updateFender}
                  removeFender={removeFender}
                  addFenderAt={addFenderAt}
                  errors={errors}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </Grid>
    </Grid>
  );
}

type SortableFenderProps = {
  fender: FenderInformation;
  index: number;
  updateFender: (index: number, field: keyof FenderInformation, value: string | number | undefined) => void;
  removeFender: (index: number) => void;
  addFenderAt: (index: number) => void;
  errors: { [key: number]: { [field: string]: string } };
};

function SortableFender({ fender, index, updateFender, removeFender, errors }: SortableFenderProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: index.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item sx={{ display: 'flex', alignItems: 'center', minWidth: 'auto' }}>
            <IconButton {...attributes} {...listeners} size="small" sx={{ padding: 0.5, cursor: 'grab' }}>
              <DragIndicatorSharp fontSize="small" />
            </IconButton>
          </Grid>
          <Grid item xs>
            <TextField
              label="Type"
              value={fender.type || ''}
              onChange={(e) => updateFender(index, 'type', e.target.value)}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs>
            <TextField
              label="Height"
              type="number"
              value={fender.height || ''}
              onChange={(e) => updateFender(index, 'height', e.target.value ? parseFloat(e.target.value) : undefined)}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs>
            <Tooltip
              title={errors[index]?.safeDeflection || ''}
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    borderColor: 'error.main',
                    backgroundColor: 'pink',
                  },
                },
              }}
            >
              <TextField
                label="Safe Deflection"
                type="number"
                value={fender.safeDeflection || ''}
                onChange={(e) => updateFender(index, 'safeDeflection', e.target.value ? parseFloat(e.target.value) : undefined)}
                size="small"
                fullWidth
                error={!!errors[index]?.safeDeflection}
              />
            </Tooltip>
          </Grid>
          <Grid item xs>
            <TextField
              label="Energy Absorption"
              type="number"
              value={fender.energyAbsorptionCapacity || ''}
              onChange={(e) => updateFender(index, 'energyAbsorptionCapacity', e.target.value ? parseFloat(e.target.value) : undefined)}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs>
            <TextField
              label="Reaction Force"
              type="number"
              value={fender.reactionForce || ''}
              onChange={(e) => updateFender(index, 'reactionForce', e.target.value ? parseFloat(e.target.value) : undefined)}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item sx={{ display: 'flex', alignItems: 'center', minWidth: 'auto' }}>
            <IconButton onClick={() => removeFender(index)} color="error" size="small" sx={{ padding: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default EditBerthDialog;
